import {
    DoppeDtoAction,
    DoppeDtoActionViewType,
    doppeDtoActionViewTypeIsDisplayedAsButton
} from '../../client-server-common/types/doppe-dto-action';
import {DoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {BymoPageViewerContextData, BymoPageViewerMode} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {BymoPageContextProps, IDoppeViewerActionContext} from '../../doppe-sdk';
import {
    DoppeActionListItemClickBehaviour,
    DoppeActionWidgetOpeningMode,
    DoppeAutomationTriggers,
    DoppeBiEmptyTargetUrl,
    DoppeDtoSpeedDialLinkViewerData,
    DoppeHtmlSiteInfo,
} from '../../client-server-common';
import {SessionStorageAccessor} from '@wix/devzai-utils-dom';
import {
    DoppePageViewerPreventedOperation
} from '../../client/bymo-page-preview-service/bymo-page-preview-host-service-api';
import {
    assertDefined,
    assertNotNullable,
    asyncDelay,
    Evaluable,
    evaluateFunction,
    evaluateWhenFunction,
    ObservableValue
} from '@wix/devzai-utils-common';
import {
    DoppeViewerModalOpeningAnimation,
    DoppeViewerModalsController
} from '../doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import React from 'react';
import {DoppeViewerActionModal} from '../doppe-viewer-action-modal/doppe-viewer-action-modal';
import {
    DoppeSearchItemViewer,
    DoppeSearchItemViewerDataActionData,
    DoppeSearchItemViewerDataActionListItemData,
    DoppeSearchItemViewerDataType,
    doppeSearchItemViewerIsOfType,
    getTargetUrlFromSearchItem
} from '../../client-server-common/types/doppe-dto-search-item-viewer-data';
import {DoppeActionWidgetPopupMode} from '../../client-server-common/types/doppe-action-widget-popup-mode';
import {DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {BymoPageLayout} from '../bymo-page-style/bymo-page-style';
import {
    DoppeViewerDefaultNotificationToast
} from '../doppe-viewer-default-notification-content/doppe-viewer-default-notification-toast';
import {
    DoppeActionGoalType,
    DoppeActionsSourceType,
    DoppeActionType,
    doppeActionTypeSupportsInPageView,
    WidgetLockController,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeViewerLockWizardModal} from '../doppe-viewer-lock-wizard-modal/doppe-viewer-lock-wizard-modal';
import {DoppeActionTypeCode} from '../../doppe-action-types/doppe-lock-action-type-code/doppe-lock-action-type-code';
import type {DoppeViewerActionsList} from '../doppe-viewer-actions-list/doppe-viewer-actions-list';
import {DoppeDtoPaymentIntentMetaData} from '../../client-server-common/types/doppe-dto-payment-intent';
import {DoppeDtoCheckoutIntent} from '../../client-server-common/types/doppe-dto-checkout-intent';
import {DoppeViewerLang} from "../../client-server-common/doppe-viewer/doppe-viewer-lang";

export class DoppeViewerActionContext<PROPS extends {}> implements IDoppeViewerActionContext<PROPS> {

    private isInModel;
    private actionIndex;
    private actionTogglingController;
    private clientServices;
    private viewerContextData;
    private bymoPageContextProps;
    private widgetRenderingContextType;
    public readonly sourceType;

    public readonly modalsController;
    public readonly lockController;

    public readonly action;

    public readonly observableCurrentItemIdToRender;
    public readonly observableIsRenderingInnerState;
    public readonly actionsListController;


    constructor(
        options: DoppeViewerActionContext.CreationOptions<PROPS>
    ) {
        this.action = options.action;
        this.actionIndex = options.actionIndex;
        this.isInModel = options.isInModel ?? false;
        this.sourceType = options.openedFromSearch ? DoppeActionsSourceType.Search : DoppeActionsSourceType.Page;

        this.actionTogglingController = options.actionTogglingController;
        this.clientServices = options.clientServices;
        this.viewerContextData = options.viewerContextData;
        this.bymoPageContextProps = options.bymoPageContextProps;
        this.modalsController = options.modalsController;
        this.lockController = options.lockController ?? null;
        this.actionsListController = options.actionsListController;

        this.observableIsRenderingInnerState = options.observableIsRenderingInnerState;
        this.observableCurrentItemIdToRender = options.observableCurrentItemIdToRender;
        this.widgetRenderingContextType = options.widgetRenderingContextType;
    }

    public async __apiHealthCheck() {
        await this.clientServices.getApiClient().health();
    }

    public isInModal() {
        return this.isInModel;
    }

    public getActionIndex() {
        return this.actionIndex;
    }

    public navigateToUrl(targetUrl: string, newTab = false) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventLinkNavigationPrevented', {
                url: targetUrl
            })
        } else {
            if (newTab) {
                window.open(targetUrl, '_blank', 'noopener');
            } else {
                location.href = targetUrl;
            }
        }
    }

    public previewAction(actionId: DoppeDtoAction.Id, options?: Parameters<IDoppeViewerActionContext<PROPS>['previewAction']>[1]) {

        const {
            closeCallback,
            showRootBackButtonInFullScreen = false,
            showRootCloseButtonInFullScreen = true,
            widgetPopupMode = DoppeActionWidgetPopupMode.Drawer,
            openedFromSearch = false,
            listItemId,
            actionIndex = -1,
            startNewContext,
            showHeader
        } = options ?? {};

        const mobileLayout = evaluateFunction(() => {
            if (widgetPopupMode === DoppeActionWidgetPopupMode.FullScreenModal ) {
                return DoppeViewerModalPanelLayout.FullScreen;
            } else if (widgetPopupMode === DoppeActionWidgetPopupMode.InnerModal) {
                return DoppeViewerModalPanelLayout.InnerCentered;
            } else if (widgetPopupMode === DoppeActionWidgetPopupMode.Drawer || widgetPopupMode === DoppeActionWidgetPopupMode.InnerFullScreenModal) {
                return DoppeViewerModalPanelLayout.Drawer;
            } else {
                return DoppeViewerModalPanelLayout.Inner;
            }
        });

        const desktopLayout = evaluateFunction(() => {
            if (widgetPopupMode === DoppeActionWidgetPopupMode.FullScreenModal) {
                return DoppeViewerModalPanelLayout.FullScreen;
            }
            else if (widgetPopupMode === DoppeActionWidgetPopupMode.InnerFullScreenModal) {
                return DoppeViewerModalPanelLayout.InnerFull;
            }
            else {
                return DoppeViewerModalPanelLayout.Inner;
            }
        })

        this.modalsController.showInnerModal({
            openingAnimation: bymoPageLayout => {
                const modalPanelLayout = bymoPageLayout === BymoPageLayout.Wide ?
                    desktopLayout :
                    mobileLayout;

                switch (modalPanelLayout) {
                    case DoppeViewerModalPanelLayout.Drawer: {
                        return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                    }
                    default: {
                        return DoppeViewerModalOpeningAnimation.FadeIn;
                    }
                }
            },
            onClosed: () => {
                if (!startNewContext) {
                    this.observableCurrentItemIdToRender?.setValue(null);
                    this.observableIsRenderingInnerState?.setValue(false);
                }
            },
            content: ({modalController}) => (
                <DoppeViewerActionModal
                    mobileLayout={mobileLayout}
                    desktopLayout={desktopLayout}
                    actionId={actionId}
                    openedFromSearch={openedFromSearch}
                    showRootBackButton={showRootBackButtonInFullScreen}
                    showRootCloseButton={showRootCloseButtonInFullScreen}
                    closeCallback={() => {
                        modalController.closeWindow();
                        closeCallback?.();
                    }}
                    defaultListItemIdToRender={listItemId}
                    actionIndex={actionIndex}
                    showHeader={showHeader}
                    actionContext={startNewContext ? null : this}
                />
            )
        })
    }

    public async performContextCallback(actionCallback: (options: {
        clientServices: DoppeViewerClientServices;
        viewerContextData: BymoPageViewerContextData;
    }) => Promise<void>) {
        return actionCallback({
            clientServices: this.clientServices,
            viewerContextData: this.viewerContextData
        })
    };

    public async performViewerOnlyCallback(actionCallback: () => Promise<void>) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {});
        } else {
            await actionCallback();
        }
    }

    public async resolveSpeedDialLinkFromCode(linkCode: string) {
        // In the future, the speed dial link resolving might be asynchronous.
        // In this case, we have to make sure that in case of an error, the returned value would be undefined.
        // This will make the resolveSpeedDialLinkFromCode function safe to be called.
        try {
            return this.bymoPageContextProps.speedDialData ?
                this.bymoPageContextProps.speedDialData.links.find(
                    link => link.speedDialCode !== null && link.speedDialCode.toLowerCase() === linkCode.toLowerCase()
                ) :
                undefined
        } catch (error) {
            return undefined;
        }
    }

    public reportSearchItemLinkClicked(
        searchItem: DoppeSearchItemViewer,
        htmlSiteInfo: DoppeHtmlSiteInfo | null,
        viewerLang: DoppeViewerLang
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            const targetUrl = getTargetUrlFromSearchItem(searchItem, this.clientServices, htmlSiteInfo, viewerLang);

            this.clientServices.viewerAnalyticsClient.logEvent('uouSpeedDialLinkRedirected', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: searchItem.id,
                destinationUrl: targetUrl ?? '',
            });

            SessionStorageAccessor.updateItem<number>(
                `speedDialRedirected: ${viewerContextData.doppeUserId} ${viewerContextData.bymoPageId} ${searchItem.id}`,
                () => Date.now()
            );
        }
    }

    public async navigateToSearchItem(
        searchItem: DoppeSearchItemViewer,
        options: {
            newTab: boolean;
            htmlSiteInfo: DoppeHtmlSiteInfo | null;
            viewerLang: DoppeViewerLang;
        }
    ) {

        const {
            newTab,
            htmlSiteInfo,
            viewerLang
        } = options;

        this.reportSearchItemLinkClicked(searchItem, htmlSiteInfo, viewerLang);
        this.reportGoalAchieved({
            goalType: DoppeActionGoalType.SearchClick,
        });

        const urlToNavigateTo = getTargetUrlFromSearchItem(searchItem, this.clientServices, htmlSiteInfo, viewerLang);

        if (urlToNavigateTo) {

            const navigateToUrlCallback = () => {
                if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionData>(searchItem, DoppeSearchItemViewerDataType.Action)) {
                    this.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                        overrideAction: {
                            actionId: searchItem.data.action.id,
                            actionIndex: searchItem.data.actionIndex,
                            actionType: searchItem.data.action.actionTypeId,
                            actionName: searchItem.title,
                        }
                    })
                } else if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionListItemData>(searchItem, DoppeSearchItemViewerDataType.ActionListItem)) {
                    this.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                        innerItemId: searchItem.data.actionListItem.itemId,
                        innerItemPosition: searchItem.data.actionListItemIndex,
                        overrideAction: {
                            actionId: searchItem.data.actionListItem.action.id,
                            actionIndex: searchItem.data.actionIndex,
                            actionType: searchItem.data.actionListItem.action.actionTypeId,
                            actionName: searchItem.title,
                        }
                    })
                }

                this.navigateToUrl(urlToNavigateTo, newTab);
            }

            const searchMainAction = evaluateFunction(() => {
                if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionData>(searchItem, DoppeSearchItemViewerDataType.Action)) {
                    return searchItem.data.action
                } else if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionListItemData>(searchItem, DoppeSearchItemViewerDataType.ActionListItem)) {
                    return searchItem.data.actionListItem.action;
                }

                return null
            })

            if (searchMainAction) {
                const observableLocked = this.clientServices.lockService.getActionLockObservable(searchMainAction);

                if (observableLocked?.getValue()) {
                    await this.startUnlocking(() => {
                        observableLocked.setValue(false)
                        // navigateToUrlCallback();
                    }, true, {
                        action: searchMainAction,
                        index: searchItem.data.actionIndex
                    });
                } else {
                    navigateToUrlCallback();
                }
            } else {
                navigateToUrlCallback();
            }

        } else {
            if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionData>(searchItem, DoppeSearchItemViewerDataType.Action)) {
                const actionType = assertDefined(
                    this.clientServices.actionTypesStore.getRegisteredActionType(searchItem.data.action.actionTypeId),
                    `Action type with id '${searchItem.data.action.actionTypeId}' isn't registered`);


                if (!actionType.skipReportingOnGoalAchievedOnActionButtonClick) {
                    this.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                        overrideAction: {
                            actionId: searchItem.data.action.id,
                            actionIndex: searchItem.data.actionIndex,
                            actionType: searchItem.data.action.actionTypeId,
                            actionName: searchItem.data.action.name,
                        }
                    })
                }

                this.previewAction(searchItem.data.action.id, {
                    openedFromSearch: true,
                    showRootBackButtonInFullScreen: true,
                    showRootCloseButtonInFullScreen: false,
                    actionIndex: searchItem.data.actionIndex,
                    startNewContext: true,
                });
            } else if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionListItemData>(searchItem, DoppeSearchItemViewerDataType.ActionListItem)) {
                const actionType = assertDefined(
                    this.clientServices.actionTypesStore.getRegisteredActionType(searchItem.data.actionListItem.action.actionTypeId),
                    `Action type with id '${searchItem.data.actionListItem.action.actionTypeId}' isn't registered`);

                this.previewAction(searchItem.data.actionListItem.action.id, {
                    openedFromSearch: true,
                    listItemId: searchItem.data.actionListItem.itemId,
                    showRootBackButtonInFullScreen: true,
                    showRootCloseButtonInFullScreen: false,
                    actionIndex: searchItem.data.actionIndex,
                    startNewContext: true,
                    widgetPopupMode: actionType.resolveCustomWidgetPopupMode?.(),
                });
            }
        }
    }

    public async navigateToSpeedDialLink(speedDialLink: DoppeDtoSpeedDialLinkViewerData, newTab: boolean) {

        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            this.clientServices.viewerAnalyticsClient.logEvent('uouSpeedDialLinkRedirected', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: speedDialLink.id,
                destinationUrl: speedDialLink.targetUrl,
            });

            SessionStorageAccessor.updateItem<number>(
                `speedDialRedirected: ${viewerContextData.doppeUserId} ${viewerContextData.bymoPageId} ${speedDialLink.id}`,
                () => Date.now()
            );

            // SessionStorageAccessor.setItem(DisableBackTrapSessionStorageKey, true);
        }

        this.navigateToUrl(speedDialLink.targetUrl, newTab);

        this.reportGoalAchieved({
            goalType: DoppeActionGoalType.SearchClick,
        });
    }

    reportButtonClicked(options: {
        buttonName: string,
        actionType: string,
    }) {
        const viewerContextData = this.viewerContextData;

        const {
            buttonName,
            actionType
        } = options;

        const sourceType = this.sourceType;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouButtonClick', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                value: buttonName,
                action: actionType,
                scope: sourceType,
                actionId: this.action.id,
            });
        }
    };


    public reportCtaButtonViewed(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType?: DoppeActionGoalType,
        buttonId: string,
        buttonIndex?: number;
    }) {
        const viewerContextData = this.viewerContextData;

        const {
            innerItemId,
            overrideAction,
            goalType = DoppeActionGoalType.Click,
            buttonId,
            buttonIndex
        } = options ?? {};


        const {
            actionId,
            actionIndex,
            actionType,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        const sourceType = this.sourceType;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouCtaButtonViewed', {
                actionId: actionId,
                innerItemId: innerItemId,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                goalType: goalType,
                goalSource: sourceType,
                ctaButtonId: buttonId,
                ctaButtonIndex: buttonIndex,
            });
        }
    };

    public reportCtaButtonClicked(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType?: DoppeActionGoalType,
        buttonId: string,
        buttonIndex?: number;
    }) {
        const viewerContextData = this.viewerContextData;

        const {
            innerItemId,
            overrideAction,
            goalType = DoppeActionGoalType.Click,
            buttonId,
            buttonIndex
        } = options ?? {};


        const {
            actionId,
            actionIndex,
            actionType,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        const sourceType = this.sourceType;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouCtaButtonClicked', {
                actionId: actionId,
                innerItemId: innerItemId,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                goalType: goalType,
                goalSource: sourceType,
                ctaButtonId: buttonId,
                ctaButtonIndex: buttonIndex,
            });
        }
    };

    public reportGoalAchieved(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType: DoppeActionGoalType,
    }) {
        const viewerContextData = this.viewerContextData;

        const {
            innerItemId,
            innerItemPosition,
            overrideAction,
            goalType = DoppeActionGoalType.Click,
        } = options ?? {};


        const {
            actionId,
            actionIndex,
            actionType,
            actionName,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
            actionName: this.action.name,
        };

        const sourceType = this.sourceType;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouActionClicked', {
                actionId: actionId,
                innerItemId: innerItemId,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                goalType: goalType,
                goalSource: sourceType,
            });

            if (innerItemId) {
                this.clientServices.viewerAnalyticsClient.logEvent('uouActionInnerItemGoalAchieved', {
                    actionId: actionId,
                    innerItemId: innerItemId,
                    innerItemPosition: innerItemPosition ?? 0,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: actionIndex,
                    actionType: actionType,
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContextData.pageType,
                    goalType: goalType,
                    goalSource: sourceType,
                });
            }

            this.clientServices.viewerAnalyticsClient.logEvent('uouAnyActionClicked', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
            });

            this.clientServices.viewerAnalyticsClient.logEvent('uouEngagementClicked', {
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                actionType: actionType,
                actionName: actionName,
            });
        }
    }

    public isLockAction() {
        return this.lockController !== null;
    }

    public async unlockLockAction(metaData: any) {

        if (this.isLockAction()) {
            const lockController = assertNotNullable(this.lockController);
            await lockController.unlock(metaData);

            const viewerContextData = this.viewerContextData;

            const {
                actionId,
                actionIndex,
                actionType,
            } = {
                actionId: this.action.id,
                actionIndex: this.actionIndex,
                actionType: this.action.actionTypeId,
            };

            if (viewerContextData.mode === BymoPageViewerMode.Live) {

                this.clientServices.viewerAnalyticsClient.logEvent('uouLockActionUnlocked', {
                    actionId: actionId,
                    lockedActionId: lockController.lockedActionId,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: actionIndex,
                    actionType: actionType,
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContextData.pageType,
                    lockSource: this.sourceType
                });
            }
        }
    }

    public async startUnlocking(onUnlocked: () => void, showUnlockPreview: boolean, overrideActionData?: {
        action: DoppeDtoAction<any>,
        index: number,
    }) {
        const viewerContextData = this.viewerContextData;

        const action = overrideActionData?.action ?? this.action;

        const unlockedMetaData = this.clientServices.lockService.getActionUnlockStateFromCookie(action.id);

        if (!showUnlockPreview && unlockedMetaData) {
            const isUnlocked = await this.unlockAction({
                overrideActionData: overrideActionData,
                unlockedCallback: onUnlocked,
                unlockedMetaData: unlockedMetaData
            });

            if (isUnlocked) {
                return;
            }
        }

        this.modalsController.showInnerModal({
            content: (renderProps) => {
                return (
                    <DoppeViewerLockWizardModal
                        showUnlockPreview={showUnlockPreview}
                        lockedActionId={action.id}
                        mobileLayout={DoppeViewerModalPanelLayout.InnerCentered}
                        desktopLayout={DoppeViewerModalPanelLayout.InnerCentered}
                        unlockedCallback={async (unlockedMetadata) => {
                            return this.unlockAction({
                                overrideActionData: overrideActionData,
                                unlockedCallback: () => {
                                    this.clientServices.lockService.updateActionUnlockStateInCookie(action.id, unlockedMetadata ?? true)
                                    onUnlocked();
                                    renderProps.modalController.closeWindow();
                                },
                                unlockedMetaData: unlockedMetadata
                            });
                        }}
                        closeCallback={() => {
                            renderProps.modalController.closeWindow();
                        }}
                    />
                )
            },
        })

        if (unlockedMetaData === undefined) {
            const {
                actionId,
                actionIndex,
                actionType,
            } = {
                actionId: this.action.id,
                actionIndex: this.actionIndex,
                actionType: this.action.actionTypeId,
            };

            if (viewerContextData.mode === BymoPageViewerMode.Live) {
                this.clientServices.viewerAnalyticsClient.logEvent('uouActionStartUnlocking', {
                    actionId: actionId,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: actionIndex,
                    actionType: actionType,
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContextData.pageType,
                    lockSource: this.sourceType
                });
            }
        }
    }

    public async unlockAction(options: {
        unlockedMetaData?: any;
        unlockedCallback?: () => void
        overrideActionData?: {
            action: DoppeDtoAction<any>,
            index: number,
        },
        isUnlockedFromCookie?: boolean
    }) {

        const {
            overrideActionData,
            unlockedCallback,
            unlockedMetaData,
            isUnlockedFromCookie = false
        } = options;

        const viewerContextData = this.viewerContextData;

        const action = overrideActionData?.action ?? this.action;

        const {
            actionId,
            actionIndex,
            actionType,
        } = {
            actionId: action.id,
            actionIndex: overrideActionData ? overrideActionData.index : this.actionIndex,
            actionType: action.actionTypeId,
        };

        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            return evaluateFunction(async () => {
                // const _unLockedAction =
                try {
                    const unlockedAction = await this.clientServices.getApiClient().unlockAction({
                        actionId: actionId,
                        pageId: viewerContextData.bymoPageId,
                        unlockedMetaData: unlockedMetaData,
                    });

                    this.clientServices.bymoService.setActionSettingsOverrides(actionId, unlockedAction.settings);

                    unlockedCallback?.();

                    if (!isUnlockedFromCookie) {
                        this.clientServices.viewerAnalyticsClient.logEvent('uouActionUnlocked', {
                            actionId: actionId,
                            pageId: viewerContextData.bymoPageId,
                            hypeUserId: viewerContextData.doppeUserId,
                            linkId: viewerContextData.linkId,
                            itemPosition: actionIndex,
                            actionType: actionType,
                            destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                            pageType: viewerContextData.pageType,
                            lockSource: this.sourceType
                        });

                        this.clientServices.viewerAnalyticsClient.logEvent('uouAnyActionUnlocked', {
                            pageId: viewerContextData.bymoPageId,
                            hypeUserId: viewerContextData.doppeUserId,
                            linkId: viewerContextData.linkId,
                            destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                            pageType: viewerContextData.pageType,
                        });
                    }
                } catch (error) {
                    return false;
                }


                return true;
            })

        } else {
            unlockedCallback?.()
            return true;
        }
    }

    public reportOnlyInnerItemClicked(options: {
        innerItemId: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
        },
        goalType: DoppeActionGoalType,
    }) {
        const {
            innerItemId,
            innerItemPosition,
            overrideAction,
            goalType = DoppeActionGoalType.Click,
        } = options;


        const viewerContextData = this.viewerContextData;

        const sourceType = this.sourceType;

        const {
            actionId,
            actionIndex,
            actionType,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouActionInnerItemGoalAchieved', {
                actionId: actionId,
                innerItemId: innerItemId,
                innerItemPosition: innerItemPosition ?? 0,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                goalSource: sourceType,
                goalType: goalType
            });
        }
    }

    public reportInnerItemViewed(innerItemId: string, innerItemPosition?: number, overrideAction?: {
        actionId: string;
        actionIndex: number;
        actionType: string;
    }) {
        const viewerContextData = this.viewerContextData;
        const sourceType = this.sourceType;

        const {
            actionId,
            actionIndex,
            actionType,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouViewedInnerItemGoalAchieved', {
                actionId: actionId,
                innerItemId: innerItemId,
                innerItemPosition: innerItemPosition ?? 0,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                viewSource: sourceType,
            });
        }
    }

    public reportInnerItemPreviewed(options: {
        innerItemId: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
        },
        previewType: DoppeActionListItemClickBehaviour;
    }) {
        const viewerContextData = this.viewerContextData;
        const sourceType = this.sourceType;
        const {
            innerItemPosition,
            innerItemId,
            overrideAction,
            previewType
        } = options;

        const {
            actionId,
            actionIndex,
            actionType,
        } = overrideAction ?? {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouActionInnerItemPreviewed', {
                actionId: actionId,
                innerItemId: innerItemId,
                innerItemPosition: innerItemPosition ?? 0,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                sourceType: sourceType,
                previewType: previewType
            });
        }
    }

    public reportActionImpression() {
        const viewerContextData = this.viewerContextData;

        const {
            actionId,
            actionIndex,
            actionType,
        } = {
            actionId: this.action.id,
            actionIndex: this.actionIndex,
            actionType: this.action.actionTypeId,
        };

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouActionImpression', {
                actionId: actionId,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                itemPosition: actionIndex,
                actionType: actionType,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContextData.pageType,
                sourceType: this.sourceType,
            });
        }
    }


    public async addContact(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string
        },
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            const {
                contactId,
                isNewContact,
            } = await this.clientServices.getApiClient().addContactV2({
                contactName: contactInfo.contactName,
                phone: contactInfo.phone,
                email: contactInfo.email,
                emailSubscription: true,
                address: contactInfo.address,
                zipcode: contactInfo.zipCode,
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                linkId: viewerContextData.linkId,
                targetUrl: this.bymoPageContextProps.targetUrl,
            });

            await this.clientServices.getApiClient().reportEventToWixAutomations({
                trigger: {
                    triggerKey: DoppeAutomationTriggers.SubscribeFormSubmitted,
                    actionId: this.action.id,
                    contactId: contactId,
                    isNewContact: isNewContact,
                }
            });

        } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {

            await asyncDelay(400);

            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {
                preventedOperation: DoppePageViewerPreventedOperation.ContactFormSubmission
            });
        }
    };

    public async submitContactForm(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string;
            listId?: string;
            message?: string;
        }
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            const {
                contactId,
                isNewContact,
            } = await this.clientServices.getApiClient().addContactV2({
                contactName: contactInfo.contactName,
                phone: contactInfo.phone,
                email: contactInfo.email,
                emailSubscription: true,
                address: contactInfo.address,
                zipcode: contactInfo.zipCode,
                message: contactInfo.message,
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                linkId: viewerContextData.linkId,
                targetUrl: this.bymoPageContextProps.targetUrl,
            });

            await this.clientServices.getApiClient().reportEventToWixAutomations({
                trigger: {
                    triggerKey: DoppeAutomationTriggers.ContactFormSubmitted,
                    actionId: this.action.id,
                    contactId: contactId,
                    isNewContact: isNewContact,
                    contactList: contactInfo.listId,
                    message: contactInfo.message,
                }
            });

            this.clientServices.viewerAnalyticsClient.logEvent('uouFormSubmitted', {
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                destinationUrl: this.bymoPageContextProps.targetUrl ?? '',
            })

        } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {

            await asyncDelay(400);

            viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventActionPrevented', {
                preventedOperation: DoppePageViewerPreventedOperation.ContactFormSubmission
            });
        }
    };


    public async submitCodeToUnlock(
        codeInfo: {
            code: string;
        }
    ) {
        const viewerContextData = this.viewerContextData;

        if (viewerContextData.mode === BymoPageViewerMode.Live) {
            const isUnlocked = await this.clientServices.getApiClient().submitCodeToUnlock({
                code: codeInfo.code,
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                linkId: viewerContextData.linkId,
                targetUrl: this.bymoPageContextProps.targetUrl,
            });

            return isUnlocked;

        } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {

            await asyncDelay(400);

            return (this.action.settings as any as DoppeActionTypeCode.ActionSettings).code === codeInfo.code;
        }

        return false;
    };

    public showNotification(
        options: {
            message: string;
            showInBottom: boolean;
        }
    ) {

        const {
            message,
            showInBottom
        } = options;

        this.modalsController.showInnerModal({
            openingAnimation: () => {
                if (showInBottom) {
                    return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                } else {
                    return DoppeViewerModalOpeningAnimation.SlideInFromTop;
                }
            },
            content: ({modalController}) => <DoppeViewerDefaultNotificationToast
                message={message}
                onClose={() => {
                    modalController.closeWindow();
                }}
            />
        });
    }

    public redirectToTargetUrl(
        options: {
            additionalMessage?: string;
            countdownSeconds?: number;
        } = {}
    ) {
        const additionalMessage = options.additionalMessage;
        const countdownSeconds = options.countdownSeconds ?? 3;

        const targetUrl = this.bymoPageContextProps.targetUrl;

        if (!targetUrl) {
            return;
        }

        const viewerContextData = this.viewerContextData;
        if (viewerContextData.mode === BymoPageViewerMode.Live) {

            this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectTriggered', {
                actionId: this.action.id,
                pageId: viewerContextData.bymoPageId,
                hypeUserId: viewerContextData.doppeUserId,
                linkId: viewerContextData.linkId,
                actionType: this.action.actionTypeId,
                contextMetaData: JSON.stringify({
                    actionId: this.action.id,
                    actionType: this.action.actionTypeId,
                }),
                destinationUrl: targetUrl,
            })
        }

        const notificationController = this.clientServices.uiService.autoRedirectNotificationOpener.open({
            targetUrl: targetUrl,
            additionalMessage: additionalMessage,
            countdownSeconds: countdownSeconds,
            onCountdownComplete: () => {

                const viewerContextData = this.viewerContextData;

                if (viewerContextData.mode === BymoPageViewerMode.Live) {

                    this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirected', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        actionType: this.action.actionTypeId,
                        contextMetaData: JSON.stringify({
                            actionId: this.action.id,
                            actionType: this.action.actionTypeId,
                        }),
                        destinationUrl: targetUrl,
                    })

                    this.clientServices.bymoService.skipToTargetUrl({
                        targetUrl: targetUrl,
                        livePageData: viewerContextData
                    })
                } else if (viewerContextData.mode === BymoPageViewerMode.LiveInApp) {
                    location.href = targetUrl;
                } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventAutoSkipPrevented', {})
                }

                notificationController.close();
            },
            onCancel: () => {
                const viewerContextData = this.viewerContextData;

                if (viewerContextData.mode === BymoPageViewerMode.Live) {

                    this.clientServices.viewerAnalyticsClient.logEvent('uouAutoRedirectCanceled', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        contextMetaData: JSON.stringify({
                            actionId: this.action.id,
                            actionType: this.action.actionTypeId,
                        }),
                        destinationUrl: targetUrl,
                    })
                } else if (viewerContextData.mode === BymoPageViewerMode.LivePreview) {
                    viewerContextData.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventAutoSkipPrevented', {})
                }

                notificationController.close();
            }
        })
    }

    public getShowActionHeader() {
        return this.action.showActionHeader !== undefined ? this.action.showActionHeader : true;
    }

    public getActionType(): DoppeActionType<PROPS> {

        const actionTypeId = this.action.actionTypeId;

        return assertDefined(
            this.clientServices.actionTypesStore.getRegisteredActionType(actionTypeId) as DoppeActionType<PROPS>,
            `Action type with id '${actionTypeId}' isn't registered`);
    }

    public getActionViewType() {
        const viewType = this.action.viewType ?? DoppeDtoActionViewType.ActionButton;
        const canRenderInPage = doppeActionTypeSupportsInPageView(this.getActionType());

        if (!canRenderInPage && !doppeDtoActionViewTypeIsDisplayedAsButton(viewType)) {
            return DoppeDtoActionViewType.ActionButton;
        } else {
            return viewType;
        }
    }

    public getWidgetRenderingContextType() {
        return evaluateWhenFunction(this.widgetRenderingContextType, this.getActionViewType())
    }

    public canBeToggled() {
        return this.actionTogglingController !== undefined &&
            doppeDtoActionViewTypeIsDisplayedAsButton(this.getActionViewType()) &&
            this.action.widgetOpeningMode !== DoppeActionWidgetOpeningMode.OpenCTA;
    }

    private getActionTogglingController() {
        return this.canBeToggled() ? this.actionTogglingController : undefined;
    }

    public setActionSettingsOverrides(overrides: PROPS) {
        this.clientServices.bymoService.setActionSettingsOverrides(this.action.id, overrides);
    }

    public toggleActionView(expand: boolean) {

        const actionsListController = this.getActionTogglingController();

        if (actionsListController) {
            const viewerContextData = this.viewerContextData;

            if (viewerContextData.mode === BymoPageViewerMode.Live) {
                this.clientServices.viewerAnalyticsClient.logEvent('uouToggleAction', {
                    actionId: this.action.id,
                    pageId: viewerContextData.bymoPageId,
                    hypeUserId: viewerContextData.doppeUserId,
                    linkId: viewerContextData.linkId,
                    itemPosition: this.actionIndex,
                    actionType: this.action.actionTypeId,
                    state: expand ? 'expand' : 'collapse',
                    destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                });

                if (!expand) {
                    this.clientServices.viewerAnalyticsClient.logEvent('uouCloseAction', {
                        actionId: this.action.id,
                        pageId: viewerContextData.bymoPageId,
                        hypeUserId: viewerContextData.doppeUserId,
                        linkId: viewerContextData.linkId,
                        itemPosition: this.actionIndex,
                        actionType: this.action.actionTypeId,
                        destinationUrl: this.bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    });
                }
            }

            switch (this.action.widgetOpeningMode) {
                case DoppeActionWidgetOpeningMode.Inline: {
                    actionsListController.toggleActionView(this.action.id, expand);
                    break;
                }
                case DoppeActionWidgetOpeningMode.Popup: {
                    if (expand) {
                        const actionType = assertDefined(
                            this.clientServices.actionTypesStore.getRegisteredActionType(this.action.actionTypeId),
                            `Action type with id '${this.action.actionTypeId}' isn't registered`);


                        this.previewAction(this.action.id, {
                            showRootBackButtonInFullScreen: false,
                            showRootCloseButtonInFullScreen: true,
                            actionIndex: this.actionIndex,
                            startNewContext: false,
                            widgetPopupMode: evaluateWhenFunction(actionType.isModalOpener) ? DoppeActionWidgetPopupMode.InnerFullScreenModal : DoppeActionWidgetPopupMode.Drawer,
                        });
                    }

                    break;
                }
                case DoppeActionWidgetOpeningMode.OpenCTA: {
                    break;
                }
            }
        }
    }

    public async createStripePaymentIntent(options: {
        innerItemId?: string,
        checkoutIntent: DoppeDtoCheckoutIntent;
    }) {
        const {
            innerItemId,
            checkoutIntent,
        } = options;

        if (this.viewerContextData.mode !== BymoPageViewerMode.Live) {
            throw new Error('Can only create stripe payment intent in live mode');
        }
        const metadata = {
            actionId: this.action.id,
            pageId: this.viewerContextData.bymoPageId,
            linkId: this.viewerContextData.linkId,
            innerItemId: innerItemId,
        } satisfies DoppeDtoPaymentIntentMetaData;

        const stripePaymentIntent = await this.clientServices.getApiClient().createStripePaymentIntent({
            checkoutIntent: checkoutIntent,
            metadata: metadata,
        });

        return assertDefined(stripePaymentIntent, 'Failed to create stripe payment intent');
    }

    public saveCheckoutIntent(options: {
        checkoutIntent: DoppeDtoCheckoutIntent;
        uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData;
        paymentIntentId: string;
        innerItemId?: string,
    }) {
        const {
            innerItemId,
            checkoutIntent,
            paymentIntentId,
            uouCheckoutData,
        } = options;

        if (this.viewerContextData.mode !== BymoPageViewerMode.Live) {
            throw new Error('Can only save checkout intent in live mode');
        }

        return this.clientServices.getApiClient().saveCheckoutIntent({
            checkoutIntent: {
                ...checkoutIntent,
                uouCheckoutData: uouCheckoutData,
                actionMetadata: {
                    actionName: this.action.name,
                    actionId: this.action.id,
                    pageId: this.viewerContextData.bymoPageId,
                    linkId: this.viewerContextData.linkId,
                    innerItemId: innerItemId,
                }
            },
            paymentIntentId: paymentIntentId,
        });
    }

    public async createWixStoresCheckoutUrl(options: {
        productId: string,
        variantId?: string,
        productProperties?: { [key: string]: string },
        customTextFields?: { [key: string]: string },
    }) {
        const {
            productId,
            variantId,
            productProperties,
            customTextFields,
        } = options;

        const {url} = await this.clientServices.getApiClient().createWixStoresCheckoutUrl({
            productId: productId,
            variantId: variantId,
            productProperties: productProperties,
            customTextFields: customTextFields,
            returnUrl: window.location.href,
        });
        return {url: url};
    }

    public getBiIdFields() {
        if (this.viewerContextData.mode === BymoPageViewerMode.Live) {
            return {
                actionId: this.action.id,
                actionType: this.action.actionTypeId,
                pageId: this.viewerContextData.bymoPageId,
                pageType: this.viewerContextData.pageType,
                linkId: this.viewerContextData.linkId,
                hypeUserId: this.viewerContextData.doppeUserId,
            };
        } else {
            throw new Error('Can only get bi id fields in live mode');
        }
    }

    public getGoogleMapsApiKey() {
        return this.clientServices.doppeUserAppData.googleMapsApiKey;
    }
}

export namespace DoppeViewerActionContext {

    export type CreationOptions<PROPS extends {}> = {
        openedFromSearch: boolean;
        action: DoppeDtoAction<PROPS>;
        actionIndex: number;
        widgetRenderingContextType: Evaluable<(viewType: DoppeDtoActionViewType) => WidgetRenderingContextType>;
        isInModel?: boolean;

        clientServices: DoppeViewerClientServices;
        actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
        modalsController: DoppeViewerModalsController;
        viewerContextData: BymoPageViewerContextData;
        bymoPageContextProps: BymoPageContextProps;
        lockController?: WidgetLockController;

        observableCurrentItemIdToRender: ObservableValue<string | null> | undefined;
        observableIsRenderingInnerState: ObservableValue<boolean>;
        actionsListController?: DoppeViewerActionsList;
    }

    export interface ActionTogglingController {

        toggleActionView(actionId: DoppeDtoAction.Id, isExpanded: boolean): void;
    }
}

export type DoppeViewerActionContextLocator = Map<DoppeDtoAction.Id, IDoppeViewerActionContext>;

export function doppeViewerActionContextLocatorCreate() {
    return new Map<DoppeDtoAction.Id, IDoppeViewerActionContext>();
}